import PatientBloodPressure from 'model/PatientBloodPressure'
import WebRepository from '../web/WebRepository'
import Api from 'clients/web/Api'
import moment from 'moment'

export class PatientBloodPressureRepository extends WebRepository<PatientBloodPressure> {
  constructor() {
    super(new Api('bp'))
  }

  async getLastValue(userId: any): Promise<any> {
    return super.search({
      limit: 1,
      sort: {
        start_timestamp: -1,
      },
      selector: {
        patientId: userId,
      },
    })
  }

  async searchByDate(
    userId: any,
    startDate: moment.Moment | undefined,
    endDate: moment.Moment | undefined,
  ): Promise<PatientBloodPressure[]> {
    let startTimeInSeconds = startDate!.valueOf() / 1000
    let endTime = endDate!.valueOf() / 1000
    let boundaries = []
    var isDayPeriod = moment(startDate).isSame(moment(endDate), 'day')

    for (var i = startTimeInSeconds; i <= endTime + 1; i += isDayPeriod ? 60 : 24 * 60 * 60)
      boundaries.push(i)

    return super.aggregateAll(
      {
        selector: [
          {
            $match: {
              patientId: userId,
              start_timestamp: {
                $gt: startDate?.unix(),
                $lt: endDate?.unix(),
              },
            },
          },
          {
            $bucket: {
              groupBy: '$start_timestamp',
              boundaries: boundaries,
              output: {
                sumsystolic: {
                  $sum: '$systolic',
                },
                sumdiastolic: {
                  $sum: '$diastolic',
                },
                count: {
                  $sum: 1,
                },
                // The measurement context of the readings that went into this bucket. A set
                // rather than a value: the bucket is an average, and if it spans a sitting and
                // a standing reading there is no single honest answer. VitalContextUtil renders
                // a lone entry and says "Mixed" for anything else.
                //
                // $ifNull, not a bare path: $addToSet drops missing fields, so a bucket holding
                // one contexted reading and one from before the feature existed would come back
                // as a clean ['sitting'] and print "Sitting" over an average that is half an
                // unknown. The explicit null keeps that bucket honest — it reads as mixed.
                positions: {
                  $addToSet: { $ifNull: ['$context.position', null] },
                },
                sites: {
                  $addToSet: { $ifNull: ['$context.site', null] },
                },
              },
            },
          },
          {
            $addFields: {
              diastolicAvg: {
                $divide: ['$sumdiastolic', '$count'],
              },
              systolicAvg: {
                $divide: ['$sumsystolic', '$count'],
              },

              start_timestamp: '$_id',
            },
          },
          {
            $project: {
              sumdiastolic: 0,
              sumsystolic: 0,
              count: 0,
              _id: 0,
            },
          },
        ],
      },
      (doc: any) => {
        return {
          start_timestamp: doc.start_timestamp,
          isDayPeriod: isDayPeriod,
          diastolicAvg: doc.diastolicAvg,
          systolicAvg: doc.systolicAvg,
          positions: doc.positions,
          sites: doc.sites,
        }
      },
    )
  }
}

export default new PatientBloodPressureRepository()
